import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popup, Position } from 'devextreme-react/popup';
import Form, {
    ButtonItem,
    GroupItem,
    SimpleItem,
    RequiredRule,
    Label,
} from 'devextreme-react/form';

import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { persianLanguage } from '../../../../constants/persianTerms';
import { useAuth } from '../../auth-provider/auth-provider';
import { BASE_API, OTP_EXPIRATION_API } from '../../../../constants/api';
import axios from 'axios';
import { persianPrice } from '../../../../shared/persian-number';
import { errorNotify } from '../../../../shared/notifications';
import { Bounce, ToastContainer } from 'react-toastify';
import GetLoginSetting from '../../../api-layer/login-settings-api/GetLoginSetting';
import { useDimension } from '../../../custom-providers/dimension-provider/DimensionProvider';

const colCountByScreen = {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
};

export default function OTPLogin(props) {
    const formRef = useRef(null);
    const auth = useAuth();
    const { windowWidth } = useDimension();

    const [remainingExpiration, setRemainingExpiration] = useState(300);
    const [popupVisible, setPopupVisible] = useState(props.popupValue);
    const [mobile, setMobile] = useState(props.mobile);
    const [incorrectOTP, setIncorrectOTP] = useState(false);
    const [positionOf, setPositionOf] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const [loginSetting, setLoginSetting] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const [formData, setFormData] = useState({
        "otp": "",
        "mobile": props.mobile,
        "hashed": props?.authChallengeResult?.hashed,
        "salt": props?.authChallengeResult?.salt,
        "number": props?.authChallengeResult?.number,
    });

    // Step 2: useEffect to set up an interval that increments the counter
    useEffect(() => {
        if (!popupVisible) return;

        if (remainingExpiration < 1){
            hideInfo();
            return;
        }

        // Create a timer to decrement the remainingExpiration every second
        const remainingDecrease = setInterval(() => {
            setRemainingExpiration(prevRemainingExpiration => prevRemainingExpiration - 1);
        }, 1000);

        // Cleanup the interval when the component unmounts to prevent memory leaks
        return () => clearInterval(remainingDecrease);
    }, [remainingExpiration, popupVisible]);

    // Step 3: Handle form input changes
    const handleChange = useCallback((e) => {
        setErrorMessage(null);
        const value = e.value || e;
        setFormData(prev => ({
            ...prev,
            otp: value
        }));
    }, []);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            mobile: props.mobile,
            hashed: props?.authChallengeResult?.hashed,
            salt: props?.authChallengeResult?.salt,
            number: props?.authChallengeResult?.number,
        }));
        setMobile(props.mobile);
        getOTPExpiration();
    }, [props.mobile, props?.authChallengeResult]);

    useEffect(() => {
        setPopupVisible(props.popupValue);
    }, [props.popupValue]);

    const handleSubmit = async (e) => {
        setIsDisabled(true);
        e.preventDefault();

        const otpValue = formData.otp;
        if (!otpValue || otpValue.length < 4) {
            setErrorMessage('لطفاً کد تأیید را وارد کنید');
            setIsDisabled(false);
            return;
        }

        try {
            const res = await auth.loginAction(formData);
            props.popupToParent(false);
            props.mobileToParent(props.mobile);
            setIsDisabled(false);
            setFormData(prev => ({ ...prev, otp: "" }));
        } catch (error) {
            setIncorrectOTP(true);
            setIsDisabled(false);
            if (error.response) {
                if (error?.response?.data && error?.response?.data?.error === 'mismatch challenge') {
                    window.location.reload();
                } else if (error?.response?.data && error?.response?.data?.non_field_errors) {
                    setErrorMessage(error?.response?.data?.non_field_errors[0]);
                } else if (error.response.status === 400) {
                    errorNotify(persianLanguage.phrase.error.ERROR_400);
                } else if (error.response.status === 401) {
                    errorNotify(persianLanguage.phrase.error.ERROR_401);
                } else if (error.response.status === 403) {
                    errorNotify(persianLanguage.phrase.error.ERROR_403);
                } else if (error.response.status === 404) {
                    errorNotify(persianLanguage.phrase.error.ERROR_404);
                } else if (error.response.status === 412) {
                    errorNotify('عدم همخوانی اطلاعات ارسالی');
                }
            }
        }
    };

    const hideInfo = () => {
        props.mobileToParent(mobile);
        setFormData(prev => ({
            ...prev,
            otp: "",
            mobile: props.mobile,
        }));
        setPopupVisible(false);
        props.popupToParent(false);
    };

    const onOptionChanged = useCallback((e) => {
        if (e.name === 'value') {
            // Handle any form value changes here if needed
        }
    }, []);

    const getOTPExpiration = () => {
        axios.get(OTP_EXPIRATION_API).then(
            response => {
                setRemainingExpiration(60 * response.data['otpExpiration']);
            }
        ).catch(
            error => {
                setRemainingExpiration(60 * 5);
            }
        );
    }

    // Submit OTP directly
    const submitOTP = async (otpCode) => {
        setIsDisabled(true);
        try {
            await auth.loginAction({
                mobile,
                otp: otpCode,
                hashed: props?.authChallengeResult?.hashed,
                salt: props?.authChallengeResult?.salt,
                number: props?.authChallengeResult?.number,
            });
            setIncorrectOTP(false);
            hideInfo();
        } catch (error) {
            setIncorrectOTP(true);
            if (error.response) {
                if (error?.response?.data && error?.response?.data?.error === 'mismatch challenge') {
                    window.location.reload();
                } else if (error?.response?.data && error?.response?.data?.non_field_errors) {
                    setErrorMessage(error?.response?.data?.non_field_errors[0]);
                } else if (error.response.status === 400) {
                    errorNotify(persianLanguage.phrase.error.ERROR_400);
                } else if (error.response.status === 401) {
                    errorNotify(persianLanguage.phrase.error.ERROR_401);
                } else if (error.response.status === 403) {
                    errorNotify(persianLanguage.phrase.error.ERROR_403);
                } else if (error.response.status === 404) {
                    errorNotify(persianLanguage.phrase.error.ERROR_404);
                } else if (error.response.status === 412) {
                    errorNotify('عدم همخوانی اطلاعات ارسالی');
                }
            }
        } finally {
            setIsDisabled(false);
        }
    };

    const getLoginSetting = () => {
        GetLoginSetting().then(
            response => {
                setLoginSetting(response.data);
            }
        ).catch(
            error => {
                // Handle error silently
            }
        );
    }

    // AUTO READ OTP (Android Chrome) + Autofill (iOS)
    useEffect(() => {
        if (!popupVisible) return;

        setTimeout(() => {
            const input = document.querySelector("input[name='otp']");
            input?.focus();
        }, 100);

        getLoginSetting();
    }, [popupVisible, mobile]);

    // AUTO READ OTP (Android Chrome) + Autofill (iOS)
    useEffect(() => {
        if (!popupVisible || !loginSetting?.webOtpApiEnabled) return;

        setTimeout(() => {
            const input = document.querySelector("input[name='otp']");
            input?.focus();
        }, 100);

        if ("OTPCredential" in window) {
            const ac = new AbortController();

            navigator.credentials
                .get({
                    otp: { transport: ["sms"] },
                    signal: ac.signal,
                })
                .then((otp) => {
                    if (otp?.code) {
                        setFormData(prev => ({ ...prev, otp: otp.code }));

                        setTimeout(() => {
                            const input = document.querySelector("input[name='otp']");
                            if (input) {
                                input.value = otp.code;
                                const event = new Event('input', { bubbles: true });
                                input.dispatchEvent(event);
                            }
                            submitOTP(otp.code);
                        }, 100);
                    }
                })
                .catch(() => { });

            return () => ac.abort();
        }
    }, [popupVisible, loginSetting?.webOtpApiEnabled]);

    // Calculate dynamic width based on screen size
    const getPopupWidth = () => {
        if (windowWidth < 640) {
            return windowWidth * 0.95; // 95% on mobile
        } else if (windowWidth < 1024) {
            return Math.min(480, windowWidth * 0.8); // 80% on tablet, max 480px
        } else {
            return 480; // Fixed 480px on desktop
        }
    };

    // Calculate dynamic height based on screen size
    const getPopupHeight = () => {
        if (windowWidth < 640) {
            return 'auto'; // Auto height on mobile
        } else {
            return 360; // Fixed height on desktop
        }
    };

    return (
        <>
            <ToastContainer
                rtl={true}
                position="top-center"
                theme="dark"
                draggable
                transition={Bounce}
            />
            <Popup
                visible={popupVisible}
                onHiding={hideInfo}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                rtlEnabled={true}
                showTitle={true}
                resizeEnabled={false}
                title={`${persianLanguage.phrase.required.ENTER_OTP_CODE}`}
                width={getPopupWidth()}
                height={getPopupHeight()}
            >
                <form onSubmit={handleSubmit} className="otp-form-container">
                    {/* Timer Display - Golden Theme */}
                    <div className="flex justify-center mb-6">
                        <div className="inline-flex flex-wrap items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-amber-50 rounded-full border border-amber-200 shadow-sm">
                            <span className="text-xs sm:text-sm font-medium text-gray-700">
                                زمان باقی مانده:
                            </span>
                            <span className="text-base sm:text-lg font-bold text-amber-600 min-w-[40px] text-center">
                                {persianPrice(remainingExpiration)}
                            </span>
                            <span className="text-xs sm:text-sm text-gray-500">ثانیه</span>
                        </div>
                    </div>

                    {/* OTP Input */}
                    <div className="mb-6">
                        <Form
                            ref={formRef}
                            formData={formData}
                            labelMode="floating"
                            readOnly={false}
                            onOptionChanged={onOptionChanged}
                            showColonAfterLabel={true}
                            showValidationSummary={true}
                        >
                            <GroupItem>
                                <SimpleItem
                                    dataField="otp"
                                    editorType="dxTextBox"
                                    editorOptions={{
                                        valueChangeEvent: 'keyup',
                                        onValueChanged: (e) => handleChange(e),
                                        elementAttr: {
                                            class: "number-input-ltr"
                                        },
                                        inputAttr: {
                                            autocomplete: "one-time-code",    // iOS
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            name: "otp",
                                        },
                                    }}
                                >
                                    <Label text={persianLanguage.word.auth.OTP}/>
                                    <RequiredRule
                                        message={persianLanguage.phrase.required.OTP_REQUIRED}
                                    />
                                </SimpleItem>
                            </GroupItem>
                        </Form>
                    </div>

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center animate-shake">
                            <p className="text-red-600 text-sm font-medium">
                                {errorMessage}
                            </p>
                        </div>
                    )}

                    {/* Submit Button - Golden Theme */}
                    <div className="mt-4">
                        <button
                            type="submit"
                            disabled={isDisabled}
                            className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:bg-amber-300 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm sm:text-base golden-button"
                            onClick={handleSubmit}
                        >
                            {isDisabled ? (
                                <>
                                    در حال ارسال...
                                </>
                            ) : (
                                persianLanguage.word.general.ENTER
                            )}
                        </button>
                    </div>
                </form>
            </Popup>

        </>
    );
}